import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const WorkersCompensation = () => {
  return (
    <div data-testid="workers-compensation-page">
      {/* Hero */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-heading font-bold text-slate-900 mb-6 tracking-tight">
            Workers' Compensation
          </h1>
          <p className="text-base md:text-xl text-slate-600 leading-relaxed">
            If you've been injured on the job in North Carolina, you have rights. Workers' compensation 
            provides medical treatment and wage replacement, but the system can be complex. We guide you 
            through every step and fight to secure the benefits you deserve.
          </p>
        </div>
      </section>

      {/* How We Help */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            How We Help
          </h2>
          <div className="space-y-4 text-slate-600 leading-relaxed">
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Assistance with filing claims and navigating the North Carolina Industrial Commission process</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Representation at hearings and appeals</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Handling of disputed claims and denials</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Coordination with medical providers to ensure proper treatment</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Pursuit of all available benefits including medical care, wage replacement, and vocational rehabilitation</span>
            </p>
            <p className="flex items-center gap-3">
              <span className="text-primary font-bold leading-none">•</span>
              <span>Evaluation of potential third-party claims for additional compensation</span>
            </p>
          </div>
        </div>
      </section>

      {/* Types of Covered Injuries */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Types of Work-Related Injuries We Handle
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Traumatic Injuries</h3>
              <p className="text-slate-600 text-sm">Falls, machinery accidents, vehicle collisions, and other sudden events</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Repetitive Stress</h3>
              <p className="text-slate-600 text-sm">Carpal tunnel, back injuries, and cumulative trauma</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Occupational Disease</h3>
              <p className="text-slate-600 text-sm">Exposure to chemicals, dust, or other workplace hazards</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Psychological Injury</h3>
              <p className="text-slate-600 text-sm">Work-related stress, PTSD, or emotional harm in limited circumstances</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Aggravation of Prior Condition</h3>
              <p className="text-slate-600 text-sm">Work activities that worsen pre-existing injuries or conditions</p>
            </div>
            <div className="bg-white border border-slate-200 p-6">
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">Fatal Workplace Accidents</h3>
              <p className="text-slate-600 text-sm">Death benefits for surviving dependents</p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Do Next */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            What to Do After a Workplace Injury
          </h2>
          <div className="space-y-6">
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Report the Injury Immediately
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Notify your employer in writing as soon as possible. Delays in reporting can jeopardize your claim.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Seek Authorized Medical Treatment
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Follow your employer's procedures for getting medical care. Keep all medical records and bills.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Document Everything
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Keep detailed records of your injury, treatment, time off work, and all communications with your employer 
                and their insurance carrier.
              </p>
            </div>
            <div className="border-l-4 border-primary pl-6">
              <h3 className="text-xl font-heading font-semibold text-slate-900 mb-2">
                Consult an Attorney Early
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Even if your claim seems straightforward, insurance companies often try to minimize or deny benefits. 
                We can help protect your rights from the start.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-12 bg-primary text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <p className="text-lg">
            Workers' compensation representation throughout <strong>North Carolina only</strong>.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-4xl font-heading font-semibold text-slate-900 mb-8 tracking-tight">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-1">
                Am I eligible for workers' compensation benefits?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Most employees in North Carolina are covered. If you were injured while performing your job duties, 
                you are likely eligible. Coverage applies regardless of who was at fault.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-2">
                What benefits am I entitled to?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Workers' compensation provides payment for all necessary medical treatment, temporary or permanent wage 
                replacement benefits (typically two-thirds of your average weekly wage), and vocational rehabilitation 
                if needed. Death benefits are available for surviving dependents.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-3">
                Can my employer fire me for filing a workers' compensation claim?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                It is illegal for an employer to retaliate against you for filing a legitimate workers' compensation claim. 
                If you believe you have been retaliated against, contact us immediately.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-4">
                What if my claim is denied?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                You have the right to appeal a denial. The process involves a hearing before the North Carolina Industrial 
                Commission. We represent clients in these hearings and work to overturn unjust denials.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="border border-slate-200 px-4 md:px-6">
              <AccordionTrigger className="text-left font-heading text-lg py-4" data-testid="faq-5">
                Can I sue my employer for a work injury?
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-4">
                Generally, workers' compensation is your exclusive remedy against your employer. However, you may have 
                a separate claim against third parties (e.g., equipment manufacturers, contractors, or other negligent parties). 
                We evaluate all potential sources of recovery.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-slate-900 mb-6 tracking-tight">
            Protect Your Rights
          </h2>
          <p className="text-lg text-slate-600 mb-8 leading-relaxed">
            Contact us for a free consultation about your North Carolina workers' compensation case.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-primary text-white px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm shadow-sm hover:bg-primary/90 transition-colors"
              data-testid="cta-consultation"
            >
              Request a Consultation
            </Link>
            <a
              href="tel:+13363541040"
              className="border-2 border-primary text-primary px-8 py-4 rounded-sm font-semibold tracking-wider uppercase text-sm hover:bg-primary/10 transition-colors"
              data-testid="cta-call"
            >
              Call 336-354-1040
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WorkersCompensation;